import { connect } from 'react-redux'
import { IObject } from 'interfaces'
import { fetch } from 'ducks/data'
import { getFormInputs } from 'ducks/formInputs'

import { getDependencies } from 'utils/dependencies'
import { isDependencyForObject } from 'utils/dependencies/dataBindings'
import { getInputsDifferent } from 'utils/dependencies/formInputs'
import { ContextConsumerComponent } from 'components/ContextConsumerComponent'

const DEFAULT_PAGE_SIZE = 20
const PREFETCH_SECOND_PAGE = true

export type PaginatedFetchProps = {
  component: IObject
  object: IObject
  globalState: any
  formInputs: any
  fetch: undefined | ((...args: any[]) => Promise<any>)
  active: boolean
}

const mapStateToProps = (state: any) => ({
  globalState: state,
  formInputs: getFormInputs(state),
})

export const connectPaginatedFetch = connect(mapStateToProps, { fetch })

class PaginatedFetchComponent<
  P extends PaginatedFetchProps,
  S extends {}
> extends ContextConsumerComponent<P, S> {
  componentDidMount() {
    if (!this.isManuallyPaginated()) {
      return
    }

    const pageSize = this.getPageSize()

    this.fetchPageData(0, pageSize).then(() => {
      if (PREFETCH_SECOND_PAGE) {
        this.fetchPageData(1, pageSize)
      }
    })
  }

  dependencyFiltersChanged(otherFormInputs: any) {
    const { component, formInputs } = this.props

    const inputsDifferent = getInputsDifferent(
      component,
      formInputs,
      otherFormInputs
    )

    return inputsDifferent
  }

  componentDidUpdate(prevProps: P) {
    // If the filters on which the table depends are updated, we need to go to the first page and fetch the data again
    const filtersUpdated = this.dependencyFiltersChanged(prevProps.formInputs)
    if (filtersUpdated) {
      this.goToFirstPage()
    } else if (!prevProps.active && this.props.active) {
      // If the screen was inactive and now is active (like when navigating to a different screen and coming back to this one),
      // re-fetch the current page data
      this.fetchCurrentPageData()
    }
  }

  getPageSize() {
    return DEFAULT_PAGE_SIZE
  }

  getCurrentPage() {
    console.warn(`Unimplemented method 'getCurrentPage'`)
    return 0
  }

  isManuallyPaginated() {
    const { object, component, globalState } = this.props
    const { getApp, getDatasources, getParams } = this.context
    const deps = getDependencies(component, {
      getApp,
      getDatasources,
      getParams,
      state: globalState,
    })

    const dependencyForTable = deps.find(dep =>
      isDependencyForObject(object.id, dep)
    )

    // External collections data is not manually paginated.
    // It should still be loaded and handled by the Screen, same as Lists

    const isExternalCollection =
      dependencyForTable?.isExternalCollection === true
    const isManuallyPaginated = !isExternalCollection

    return isManuallyPaginated
  }

  goToFirstPage() {
    console.warn(`Unimplemented method 'goToFirstPage'`)
  }

  fetchPageData(page: number, limit = DEFAULT_PAGE_SIZE) {
    if (!this.isManuallyPaginated()) {
      // No manual fetching if the data is not manually paginated
      return Promise.resolve()
    }

    const { component, globalState, fetch, object } = this.props
    const { getBaseURL, getApp, getDatasources, getParams } = this.context

    if (!fetch) {
      throw new Error(
        `PaginatedFetchComponent is missing 'fetch' prop, make sure to use connectPaginatedFetch HOC`
      )
    }

    const url = getBaseURL()

    const componentDependencies = getDependencies(component, {
      getDatasources,
      getApp,
      state: globalState,
      getParams,
    })

    // Filter the dependencies to only the ones that are for the current object
    const objectDependencies = componentDependencies.filter(dep =>
      isDependencyForObject(object.id, dep)
    )

    const paginationOptions = { offset: page * limit, limit }

    return Promise.all(
      objectDependencies.map(dep =>
        fetch(url, { ...dep, ...paginationOptions })
      )
    )
  }

  fetchCurrentPageData() {
    const page = this.getCurrentPage()
    const pageSize = this.getPageSize()
    return this.fetchPageData(page, pageSize)
  }
}

export default PaginatedFetchComponent
